function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import ServiceFactory from "../../service";

/*
 * Content Hub Library tags (DS-approved mockup PlunkDocs/mockups/content-library-tags-2026-10-08.html).
 * Tags belong to the piece (container) and are shared across the workspace.
 *
 *   GET    /v1/content/tags?assignments=true      {items:[{id,name,color,hidden,pieces,lastUsed}], assignments:{containerId:[tagId]}}
 *   POST   /v1/content/tags {name,color?,hidden?}  create (an existing name returns that tag)
 *   PUT    /v1/content/tags/{id} {name?,color?,hidden?}
 *   POST   /v1/content/tags/{id}/merge {intoId}
 *   DELETE /v1/content/tags/{id}
 *   PUT    /v1/content/containers/{id}/tags {tagIds}
 *   POST   /v1/content/tags/bulk {containerIds,add,remove}
 */

export const TAG_PALETTE = [["#ff8a80", "Coral"], ["#f2b84b", "Amber"], ["#3fa37a", "Green"], ["#5b8def", "Blue"], ["#9b7bd8", "Violet"], ["#c97b3b", "Rust"], ["#d9534f", "Red"], ["#8a94a6", "Slate"]];
export const UNTAGGED = "__untagged__";
function svc(api) {
  const S = ServiceFactory.getType(api, "product");
  return new S();
}
export function toast(msg) {
  const t = document.querySelector("paper-toast");
  if (t) t.show(msg);
}
const errMsg = (e, fallback) => e && (e.message || e.error) || fallback;
export const tagsApi = {
  list: () => svc("/content/tags?assignments=true").getAsync(),
  create: name => svc("/content/tags").postAsync({
    name
  }),
  update: (id, patch) => svc(`/content/tags/${encodeURIComponent(id)}`).putAsync(patch),
  merge: (id, intoId) => svc(`/content/tags/${encodeURIComponent(id)}/merge`).postAsync({
    intoId
  }),
  remove: id => svc(`/content/tags/${encodeURIComponent(id)}`).deleteAsync(),
  setPiece: (containerId, tagIds) => svc(`/content/containers/${String(containerId)}/tags`).putAsync({
    tagIds
  }),
  // Container ids exceed 2^53: send them as unquoted number literals in a string body (postAsync sends it verbatim).
  bulk: (containerIds, add, remove) => svc("/content/tags/bulk").postAsync(`{"containerIds":[${containerIds.map(String).join(",")}],"add":${JSON.stringify(add || [])},"remove":${JSON.stringify(remove || [])}}`)
};

/** Start-of-word match: every word typed must start a word of the tag name ("fall launch" finds "Fall 2026 launch"). */
export function tagMatches(name, q) {
  const query = String(q || "").trim().toLowerCase().replace(/^#/, "");
  if (!query) return true;
  const words = String(name || "").toLowerCase().split(/[\s\-_/.,:;&+]+/).filter(Boolean);
  return query.split(/\s+/).filter(Boolean).every(w => words.some(x => x.startsWith(w)));
}

/** The name with the matched word starts in bold. */
function Highlight({
  name,
  q
}) {
  const qs = String(q || "").trim().toLowerCase().replace(/^#/, "").split(/\s+/).filter(Boolean);
  if (!qs.length) return /*#__PURE__*/React.createElement(React.Fragment, null, name);
  const parts = String(name).split(/(\s+)/);
  return /*#__PURE__*/React.createElement(React.Fragment, null, parts.map((p, i) => {
    const w = qs.find(x => p.toLowerCase().startsWith(x));
    return w ? /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, /*#__PURE__*/React.createElement("b", null, p.slice(0, w.length)), p.slice(w.length)) : /*#__PURE__*/React.createElement(React.Fragment, {
      key: i
    }, p);
  }));
}
export const tagDot = (color, size) => /*#__PURE__*/React.createElement("span", {
  className: "hub-tag-dot",
  style: {
    background: color || "#8a94a6",
    width: size || 8,
    height: size || 8
  }
});

/** Soft chip in the tag's color (works on light and dark surfaces). */
export function TagChip({
  tag,
  onRemove,
  small
}) {
  const c = tag && tag.color || "#8a94a6";
  return /*#__PURE__*/React.createElement("span", {
    className: "hub-tag-chip" + (small ? " sm" : ""),
    title: tag.hidden ? `${tag.name} (hidden unless filtered)` : tag.name,
    style: {
      background: `color-mix(in srgb, ${c} 18%, var(--plk-surface))`,
      color: `color-mix(in srgb, ${c} 45%, var(--plk-text))`
    }
  }, tagDot(c, 6), /*#__PURE__*/React.createElement("span", {
    className: "n"
  }, tag.name), onRemove && /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": `Remove ${tag.name}`,
    onClick: e => {
      e.stopPropagation();
      onRemove();
    }
  }, "\xD7"));
}
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

/**
 * "+ tag" popover on a Library card: type to search (start of word), existing tags first with piece counts, then
 * Create tag "x". Enter adds, Backspace removes the last tag, Escape or Done closes. Saved as you pick (the parent
 * calls the API on every add/remove).
 */
export class TagPicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      q: "",
      active: 0,
      busy: false
    });
    _defineProperty(this, "onDoc", e => {
      if (this._root && !this._root.contains(e.target)) this.props.onClose();
    });
    _defineProperty(this, "pick", async opt => {
      if (!opt || this.state.busy) return;
      if (opt.kind === "tag") {
        this.setState({
          q: "",
          active: 0
        });
        this.props.onAdd(opt.tag.id);
        return;
      }
      this.setState({
        busy: true
      });
      try {
        const t = await this.props.onCreate(opt.name);
        if (t && t.id) this.props.onAdd(String(t.id));
        this.setState({
          q: "",
          active: 0
        });
      } catch (e) {
        toast(errMsg(e, "Could not create that tag."));
      } finally {
        this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "onKey", e => {
      const opts = this.options();
      if (e.key === "Enter") {
        e.preventDefault();
        this.pick(opts[Math.min(this.state.active, opts.length - 1)]);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setState({
          active: Math.min(this.state.active + 1, Math.max(0, opts.length - 1))
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setState({
          active: Math.max(0, this.state.active - 1)
        });
      } else if (e.key === "Backspace" && !this.state.q && (this.props.value || []).length) {
        e.preventDefault();
        const v = this.props.value;
        this.props.onRemove(v[v.length - 1]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.props.onClose();
      }
    });
  }
  componentDidMount() {
    document.addEventListener("mousedown", this.onDoc);
  }
  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDoc);
  }
  options() {
    const {
      tags,
      value
    } = this.props;
    const q = this.state.q.trim();
    const on = new Set(value || []);
    const matches = (tags || []).filter(t => !on.has(t.id) && tagMatches(t.name, q)).sort((a, b) => (Number(b.pieces) || 0) - (Number(a.pieces) || 0) || a.name.localeCompare(b.name)).slice(0, 8);
    const opts = matches.map(t => ({
      kind: "tag",
      tag: t
    }));
    const clean = q.replace(/^#/, "").trim();
    if (clean && !(tags || []).some(t => t.name.toLowerCase() === clean.toLowerCase())) opts.push({
      kind: "create",
      name: clean
    });
    return opts;
  }
  render() {
    const {
      tags,
      value,
      onRemove,
      onClose
    } = this.props;
    const {
      q,
      active,
      busy
    } = this.state;
    const byId = new Map((tags || []).map(t => [t.id, t]));
    const opts = this.options();
    const existing = opts.filter(o => o.kind === "tag");
    const create = opts.find(o => o.kind === "create");
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-pop",
      ref: el => this._root = el,
      onClick: e => e.stopPropagation(),
      onMouseDown: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-pop-input"
    }, (value || []).map(id => byId.get(id)).filter(Boolean).map(t => /*#__PURE__*/React.createElement(TagChip, {
      key: t.id,
      tag: t,
      onRemove: () => onRemove(t.id)
    })), /*#__PURE__*/React.createElement("input", {
      autoFocus: true,
      value: q,
      placeholder: (value || []).length ? "" : "Add a tag",
      "aria-label": "Add a tag",
      disabled: busy,
      onChange: e => this.setState({
        q: e.target.value,
        active: 0
      }),
      onKeyDown: this.onKey
    })), existing.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-pop-list"
    }, /*#__PURE__*/React.createElement("span", {
      className: "hub-tag-pop-h"
    }, "Existing tags"), existing.map((o, i) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: o.tag.id,
      className: i === active ? "on" : "",
      onMouseEnter: () => this.setState({
        active: i
      }),
      onClick: () => this.pick(o)
    }, tagDot(o.tag.color), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, /*#__PURE__*/React.createElement(Highlight, {
      name: o.tag.name,
      q: q
    })), /*#__PURE__*/React.createElement("span", {
      className: "c"
    }, plural(Number(o.tag.pieces) || 0, "piece"))))), create && /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-pop-list"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "create" + (active === existing.length ? " on" : ""),
      onMouseEnter: () => this.setState({
        active: existing.length
      }),
      onClick: () => this.pick(create)
    }, /*#__PURE__*/React.createElement("span", {
      className: "hub-tag-dot new"
    }), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Create tag \"", create.name, "\""))), !existing.length && !create && /*#__PURE__*/React.createElement("p", {
      className: "hub-tag-pop-empty"
    }, (tags || []).length ? "Every tag is already on this piece. Type a new name to create one." : "No tags yet. Type a name to create the first one."), /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-pop-foot"
    }, /*#__PURE__*/React.createElement("span", null, "Enter adds \xB7 Backspace removes the last tag"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base",
      onClick: onClose
    }, "Done")));
  }
}

/**
 * Select mode "Tags" panel: every tag as a checkbox with what Apply will do ("add to 3 · 1 has it").
 * A tag some of the pieces have starts mixed (no change); a click cycles mixed → add to all → remove from all.
 */
export class BulkTagPanel extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "create", async () => {
      const name = this.state.q.replace(/^#/, "").trim();
      if (!name) return;
      this.setState({
        busy: true
      });
      try {
        const t = await this.props.onCreate(name);
        if (t && t.id) this.setState({
          q: "",
          pick: {
            ...this.state.pick,
            [String(t.id)]: "on"
          }
        });
      } catch (e) {
        toast(errMsg(e, "Could not create that tag."));
      } finally {
        this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "apply", async () => {
      const {
        add,
        remove
      } = this.changes();
      if (!add.length && !remove.length) {
        this.props.onClose();
        return;
      }
      this.setState({
        busy: true
      });
      try {
        await this.props.onApply(add, remove);
        this.props.onClose();
      } catch (e) {
        toast(errMsg(e, "Could not change the tags."));
        this.setState({
          busy: false
        });
      }
    });
    this.state = {
      q: "",
      pick: {},
      busy: false
    };
  }
  have(tagId) {
    return (this.props.pieces || []).filter(c => (c.tags || []).includes(tagId)).length;
  }
  stateOf(tagId) {
    if (this.state.pick[tagId] !== undefined) return this.state.pick[tagId];
    const h = this.have(tagId);
    const n = (this.props.pieces || []).length;
    return h === 0 ? "off" : h === n ? "on" : "mixed";
  }
  toggle(tagId) {
    const h = this.have(tagId);
    const n = (this.props.pieces || []).length;
    const base = h === 0 ? "off" : h === n ? "on" : "mixed";
    const cur = this.stateOf(tagId);
    const next = cur === "mixed" ? "on" : cur === "on" ? "off" : base === "mixed" ? "mixed" : "on";
    const pick = {
      ...this.state.pick
    };
    if (next === base) delete pick[tagId];else pick[tagId] = next;
    this.setState({
      pick
    });
  }
  changes() {
    const n = (this.props.pieces || []).length;
    const add = [];
    const remove = [];
    Object.keys(this.state.pick).forEach(id => {
      const st = this.state.pick[id];
      const h = this.have(id);
      if (st === "on" && h < n) add.push(id);
      if (st === "off" && h > 0) remove.push(id);
    });
    return {
      add,
      remove
    };
  }
  render() {
    const {
      tags,
      pieces,
      onClose
    } = this.props;
    const {
      q,
      busy
    } = this.state;
    const n = (pieces || []).length;
    const clean = q.replace(/^#/, "").trim();
    const list = (tags || []).filter(t => tagMatches(t.name, q)).sort((a, b) => this.have(b.id) - this.have(a.id) || (Number(b.pieces) || 0) - (Number(a.pieces) || 0) || a.name.localeCompare(b.name));
    const canCreate = clean && !(tags || []).some(t => t.name.toLowerCase() === clean.toLowerCase());
    const {
      add,
      remove
    } = this.changes();
    const byId = new Map((tags || []).map(t => [t.id, t]));
    const summary = [...add.map(id => `Adds ${(byId.get(id) || {}).name} to ${plural(n - this.have(id), "piece")}`), ...remove.map(id => `Removes ${(byId.get(id) || {}).name} from ${plural(this.have(id), "piece")}`)];
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-bulk",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("b", {
      className: "hub-tag-bulk-h"
    }, "Tags for ", plural(n, "piece")), /*#__PURE__*/React.createElement("input", {
      className: "hub-tag-bulk-q",
      value: q,
      placeholder: "Find or create a tag",
      "aria-label": "Find or create a tag",
      autoFocus: true,
      onChange: e => this.setState({
        q: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter" && canCreate) {
          e.preventDefault();
          this.create();
        }
        if (e.key === "Escape") onClose();
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-bulk-list"
    }, list.map(t => {
      const st = this.stateOf(t.id);
      const h = this.have(t.id);
      const info = st === "on" ? h === n ? n === 1 ? "has it" : "all have it" : `add to ${n - h}${h ? ` · ${h} ${h === 1 ? "has" : "have"} it` : ""}` : st === "off" ? h > 0 ? `remove from ${h}` : "none have it" : `${h} of ${n} have it`;
      return /*#__PURE__*/React.createElement("label", {
        key: t.id,
        className: st !== "off" ? "on" : ""
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: st === "on",
        ref: el => {
          if (el) el.indeterminate = st === "mixed";
        },
        onChange: () => this.toggle(t.id)
      }), tagDot(t.color), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, t.name), /*#__PURE__*/React.createElement("span", {
        className: "c"
      }, info));
    }), canCreate && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "create",
      disabled: busy,
      onClick: this.create
    }, /*#__PURE__*/React.createElement("span", {
      className: "hub-tag-dot new"
    }), "Create tag \"", clean, "\""), !list.length && !canCreate && /*#__PURE__*/React.createElement("p", {
      className: "hub-tag-pop-empty"
    }, "No tags yet. Type a name to create one.")), /*#__PURE__*/React.createElement("div", {
      className: "hub-tag-bulk-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, summary.length ? summary.slice(0, 2).join(" · ") + (summary.length > 2 ? ` · and ${summary.length - 2} more` : "") : "Tick a tag to add it, untick to remove it"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      disabled: busy,
      onClick: onClose
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || !summary.length,
      onClick: this.apply
    }, busy ? "Applying…" : "Apply")));
  }
}
function fmtDay(d) {
  if (!d) return "Never";
  const x = String(d);
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(x) ? x : x.replace(" ", "T") + "Z");
  return isNaN(t) ? "" : t.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: t.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  });
}

/** Manage tags: rename and recolor inline, merge into another tag, delete, and "Hidden unless filtered". */
export class ManageTagsSheet extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      edit: null,
      merge: null,
      del: null,
      adding: false,
      newName: "",
      busy: false
    });
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape" && !this.state.busy && !this.state.edit && !this.state.merge && !this.state.del) this.props.onClose();
    });
    _defineProperty(this, "run", async (fn, okMsg) => {
      this.setState({
        busy: true
      });
      try {
        await fn();
        if (okMsg) toast(okMsg);
        await this.props.onChanged();
        this.setState({
          edit: null,
          merge: null,
          del: null,
          adding: false,
          newName: ""
        });
      } catch (e) {
        toast(errMsg(e, "That did not work. Try again."));
      } finally {
        this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "saveEdit", () => {
      const {
        edit
      } = this.state;
      const t = (this.props.tags || []).find(x => x.id === edit.id);
      if (!t) return;
      const patch = {};
      const name = edit.name.trim();
      if (!name) {
        toast("A tag needs a name.");
        return;
      }
      if (name !== t.name) patch.name = name;
      if (edit.color !== t.color) patch.color = edit.color;
      if (!Object.keys(patch).length) {
        this.setState({
          edit: null
        });
        return;
      }
      this.run(() => tagsApi.update(t.id, patch), "Tag saved.");
    });
  }
  componentDidMount() {
    document.addEventListener("keydown", this.onKey);
  }
  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKey);
  }
  render() {
    const {
      tags,
      onClose
    } = this.props;
    const {
      edit,
      merge,
      del,
      adding,
      newName,
      busy
    } = this.state;
    const rows = (tags || []).slice().sort((a, b) => (Number(b.pieces) || 0) - (Number(a.pieces) || 0) || a.name.localeCompare(b.name));
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-edit-backdrop",
      onClick: () => {
        if (!busy) onClose();
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tags-sheet",
      onClick: e => e.stopPropagation(),
      role: "dialog",
      "aria-label": "Manage tags"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tags-sheet-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "eyebrow"
    }, "Content Hub \xB7 Library"), /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3"
    }, "Manage tags"), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Rename, recolor, merge duplicates, and keep working tags out of the Library unless you filter for them.")), /*#__PURE__*/React.createElement("span", {
      className: "acts"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: onClose
    }, "Back to Library"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy,
      onClick: () => this.setState({
        adding: true,
        newName: ""
      })
    }, "New tag"))), /*#__PURE__*/React.createElement("div", {
      className: "hub-tags-table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tags-row head"
    }, /*#__PURE__*/React.createElement("span", null, "Tag"), /*#__PURE__*/React.createElement("span", null, "Pieces"), /*#__PURE__*/React.createElement("span", null, "Last used"), /*#__PURE__*/React.createElement("span", null, "In the Library"), /*#__PURE__*/React.createElement("span", {
      className: "r"
    }, "Actions")), adding && /*#__PURE__*/React.createElement("div", {
      className: "hub-tags-row editing"
    }, /*#__PURE__*/React.createElement("span", {
      className: "name"
    }, /*#__PURE__*/React.createElement("span", {
      className: "hub-tag-dot new"
    }), /*#__PURE__*/React.createElement("input", {
      autoFocus: true,
      value: newName,
      placeholder: "Tag name",
      "aria-label": "Tag name",
      maxLength: 48,
      onChange: e => this.setState({
        newName: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter" && newName.trim()) this.run(() => tagsApi.create(newName.trim()), "Tag created.");
        if (e.key === "Escape") this.setState({
          adding: false
        });
      }
    })), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", {
      className: "r"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base",
      onClick: () => this.setState({
        adding: false
      })
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || !newName.trim(),
      onClick: () => this.run(() => tagsApi.create(newName.trim()), "Tag created.")
    }, "Create"))), !rows.length && !adding && /*#__PURE__*/React.createElement("p", {
      className: "hub-tag-pop-empty",
      style: {
        padding: "14px 18px"
      }
    }, "No tags yet. Use + tag on any piece in the Library, or New tag here."), rows.map(t => {
      const n = Number(t.pieces) || 0;
      const isEdit = edit && edit.id === t.id;
      const isMerge = merge && merge.id === t.id;
      const isDel = del === t.id;
      const hiddenBox = /*#__PURE__*/React.createElement("label", {
        className: "hid"
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        checked: !!t.hidden,
        disabled: busy,
        onChange: e => this.run(() => tagsApi.update(t.id, {
          hidden: e.target.checked
        }), e.target.checked ? `${t.name} is hidden unless filtered.` : `${t.name} shows in the Library.`)
      }), "Hidden unless filtered");
      const target = isMerge ? rows.find(x => x.id === merge.into) : null;
      return /*#__PURE__*/React.createElement("div", {
        key: t.id,
        className: "hub-tags-item" + (isEdit || isMerge || isDel ? " active" : "")
      }, /*#__PURE__*/React.createElement("div", {
        className: "hub-tags-row"
      }, isEdit ? /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, tagDot(edit.color, 10), /*#__PURE__*/React.createElement("input", {
        autoFocus: true,
        value: edit.name,
        "aria-label": "Tag name",
        maxLength: 48,
        onChange: e => this.setState({
          edit: {
            ...edit,
            name: e.target.value
          }
        }),
        onKeyDown: e => {
          if (e.key === "Enter") this.saveEdit();
          if (e.key === "Escape") this.setState({
            edit: null
          });
        }
      })) : /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, tagDot(t.color, 10), t.name), /*#__PURE__*/React.createElement("span", null, n), /*#__PURE__*/React.createElement("span", {
        className: "muted"
      }, fmtDay(t.lastUsed)), /*#__PURE__*/React.createElement("span", null, hiddenBox), /*#__PURE__*/React.createElement("span", {
        className: "r"
      }, isEdit ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          edit: null
        })
      }, "Cancel"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
        disabled: busy,
        onClick: this.saveEdit
      }, "Save")) : isMerge ? /*#__PURE__*/React.createElement("b", {
        className: "state"
      }, "Merging") : isDel ? /*#__PURE__*/React.createElement("b", {
        className: "state danger"
      }, "Deleting") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "lnk",
        onClick: () => this.setState({
          edit: {
            id: t.id,
            name: t.name,
            color: t.color
          },
          merge: null,
          del: null
        })
      }, "Rename"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "lnk",
        disabled: rows.length < 2,
        onClick: () => this.setState({
          merge: {
            id: t.id,
            into: (rows.find(x => x.id !== t.id) || {}).id
          },
          edit: null,
          del: null
        })
      }, "Merge"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "lnk danger",
        onClick: () => this.setState({
          del: t.id,
          edit: null,
          merge: null
        })
      }, "Delete")))), isEdit && /*#__PURE__*/React.createElement("div", {
        className: "hub-tags-colors"
      }, /*#__PURE__*/React.createElement("span", null, "Color"), TAG_PALETTE.map(([hex, label]) => /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: hex,
        "aria-label": label + (edit.color === hex ? ", selected" : ""),
        title: label,
        className: edit.color === hex ? "on" : "",
        style: {
          background: hex
        },
        onClick: () => this.setState({
          edit: {
            ...edit,
            color: hex
          }
        })
      }))), isMerge && /*#__PURE__*/React.createElement("div", {
        className: "hub-tags-merge"
      }, /*#__PURE__*/React.createElement("span", null, "Merge ", /*#__PURE__*/React.createElement("b", null, t.name), " into"), /*#__PURE__*/React.createElement("select", {
        "aria-label": "Target tag",
        value: merge.into || "",
        onChange: e => this.setState({
          merge: {
            ...merge,
            into: e.target.value
          }
        })
      }, rows.filter(x => x.id !== t.id).map(x => /*#__PURE__*/React.createElement("option", {
        key: x.id,
        value: x.id
      }, x.name))), /*#__PURE__*/React.createElement("span", {
        className: "muted"
      }, plural(n, "piece"), " move over. ", t.name, " is removed. Pieces that already have both keep one."), /*#__PURE__*/React.createElement("span", {
        className: "sp"
      }), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          merge: null
        })
      }, "Cancel"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
        disabled: busy || !merge.into,
        onClick: () => this.run(() => tagsApi.merge(t.id, merge.into), `Merged ${t.name} into ${target ? target.name : "the other tag"}.`)
      }, "Merge ", plural(n, "piece"))), isDel && /*#__PURE__*/React.createElement("div", {
        className: "hub-tags-merge del"
      }, /*#__PURE__*/React.createElement("span", null, "Delete ", /*#__PURE__*/React.createElement("b", null, t.name), "? It comes off ", plural(n, "piece"), ". The pieces themselves stay."), /*#__PURE__*/React.createElement("span", {
        className: "sp"
      }), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base",
        onClick: () => this.setState({
          del: null
        })
      }, "Cancel"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
        disabled: busy,
        onClick: () => this.run(() => tagsApi.remove(t.id), `Deleted ${t.name}.`)
      }, "Delete tag")));
    })), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "12px 2px 0"
      }
    }, "Delete removes the tag from every piece; the pieces themselves stay. Tags are shared by everyone in the workspace.")));
  }
}